import {
  customElement,
  FASTElement,
  Observable,
  observable,
  Splice,
} from '@microsoft/fast-element';
import { AllContractsGraphTemplate as template } from './all-contracts-graph.template';
import { AllContractsGraphStyles as styles } from './all-contracts-graph.styles';
import { IBuyerAnalyticsService } from '../../../../../services';
import { VppaRankings } from '../../../../../types';
import Highcharts, { Chart, SeriesOptionsType } from 'highcharts';
import { PRODUCT_TYPE } from '../../../../../utils';
import { getFormattedNumber, getPriceValue } from '../../../../../utils/formatting';

enum graphColors {
  PROJECT_ADDITIONALITY = '#2ECC71',
  IMPLIED_CARBON_PRICE = '#1D5F55',
  LME = '#16A085',
  EXPECTED_SETTLEMENT = '#245070',
  LOCATION = '#2980B9',
}

const name = 'all-contracts-graph';

@customElement({
  name,
  template,
  styles,
})
export class AllContractsGraph extends FASTElement {
  @IBuyerAnalyticsService buyerAnalyticsService: IBuyerAnalyticsService;
  @observable vppaRankings: VppaRankings[];
  @observable allcontractsGraph!: HTMLDivElement;
  @observable chart: Chart;
  @observable showGraph: boolean = false;

  private notifier1: any;
  private handler1: any;

  connectedCallback(): void {
    super.connectedCallback();
    this.loadVppaRankings();
    this.renderAllContractsGraph();

    window.addEventListener('resize', () => {
      this.reflow();
    });
    setTimeout(() => {
      if (this.buyerAnalyticsService.selectedVppas !== undefined) {
        if (this.buyerAnalyticsService.selectedVppas.length > 0) {
          this.vppaRankings = this.buyerAnalyticsService.vppaRankings;
          Observable.notify(this.buyerAnalyticsService, 'vppaRankings');
        }
      }
    }, 10);
  }

  private loadVppaRankings() {
    const context = this;
    this.notifier1 = Observable.getNotifier(this.buyerAnalyticsService);
    this.handler1 = {
      handleChange(source: any, splices: Splice[]) {
        context.vppaRankings = source.vppaRankings;
      },
    };

    this.notifier1.subscribe(this.handler1, 'vppaRankings');
  }

  private renderAllContractsGraph() {
    const context = this;

    if (this.vppaRankings && this.buyerAnalyticsService.currentPreferences) {
      const xAxisTitlesMap = new Map();
      let series;
      if (this.vppaRankings.length > 0) {
        series = this.getSeries();
        this.showGraph = true;
      } else if (this.chart) {
        this.showGraph = false;
        return;
      }

      this.vppaRankings.forEach((vppaRanking) =>
        xAxisTitlesMap.set(vppaRanking.UNIQUE_ID + '-' + vppaRanking.PROJECT_NAME, vppaRanking),
      );
      this.chart = Highcharts.chart({
        chart: {
          renderTo: this.allcontractsGraph,
          type: 'bar',
          events: {
            render: (event) => context.checkOnEvent(event),
          },
        },
        title: {
          text: '',
        },
        boost: {
          seriesThreshold: 5,
          usePreallocated: true,
        },
        lang: {
          noData: 'No Data Found, Please try to plot chart again with different configuration.',
        },
        noData: {
          style: {
            fontWeight: 'bold',
            fontSize: '15px',
            color: '#303030',
          },
        },
        xAxis: {
          categories: [...xAxisTitlesMap.keys()],
          lineWidth: 0,
          minorGridLineWidth: 0,
          lineColor: 'transparent',
        },
        yAxis: {
          min: 0,
          opposite: true,
          lineWidth: 2,
          title: {
            text: null,
          },
        },
        tooltip: {
          formatter: function () {
            var tooltip = this.x.toString();
            const vppaRanking = xAxisTitlesMap.get(this.x);
            tooltip += `<br><span style="color: ${graphColors.PROJECT_ADDITIONALITY};">&#9679;</span>`;
            tooltip += ' <b>Project Additionality : </b>' + vppaRanking.PROJECT_OPERATIONAL_STATUS;
            tooltip += `<br><span style="color: ${graphColors.IMPLIED_CARBON_PRICE};">&#9679;</span>`;
            tooltip +=
              ' <b>Implied Carbon Price : </b>' +
              getPriceValue(vppaRanking.REFERENCED_IMPLIED_CARBON_PRICE, '', '$');
            tooltip += `<br><span style="color: ${graphColors.LME};">&#9679;</span>`;
            tooltip += ' <b>LME : </b>' + getFormattedNumber(vppaRanking.REFERENCED_LME, '', 2);
            tooltip += `<br><span style="color: ${graphColors.EXPECTED_SETTLEMENT};">&#9679;</span>`;
            tooltip +=
              ' <b>Expected Settlement : </b>' +
              getPriceValue(vppaRanking.REFERENCED_EXPECTED_SETTLEMENT, '', '$');
            tooltip += `<br><span style="color: ${graphColors.LOCATION};">&#9679;</span>`;
            tooltip += ' <b>Location (ISO) : </b>' + vppaRanking.ISO;

            return tooltip;
          },
        },
        legend: {
          reversed: true,
        },
        plotOptions: {
          bar: {
            borderWidth: 0,
            pointWidth: 10,
            groupPadding: 7,
          },
          series: {
            stacking: 'normal',
            cursor: 'pointer',
            dataLabels: {
              enabled: false,
            },
            point: {
              events: {
                click: function (event) {
                  const _selected = !this.selected;

                  if (
                    (context.buyerAnalyticsService.selectedVppas.length < 10 && _selected) ||
                    !_selected
                  ) {
                    this.select(_selected, true);
                    this.series.chart.hoverPoints.forEach((point) => point.select(_selected, true));

                    const selectedPoints = this.series.chart.getSelectedPoints();
                    const vppaTickers = [...new Set(selectedPoints.map((item) => item.category))];

                    context.buyerAnalyticsService.filterVppaRankings(vppaTickers);
                    if (selectedPoints.length) {
                      context.$emit('enableProceedButton', true);
                    } else {
                      context.$emit('enableProceedButton', false);
                    }
                  }
                },
              },
            },
          },
        },
        series: series,
      });
    }
  }

  public reflow() {
    if (this.chart) {
      this.renderAllContractsGraph();
    }
  }

  private getSeries(): SeriesOptionsType[] {
    const data = this.getValuesFromData();

    const projectAdditionality = {
      type: 'bar',
      name: 'Project Additionality',
      selected: false,
      data: data.PROJECT_ADDITIONALITY,
      color: graphColors.LOCATION,
      states: {
        select: {
          color: '#60db94',
        },
        hover: {
          color: '#60db94',
        },
      },
    } as SeriesOptionsType;

    const impliedCarbonPrice = {
      type: 'bar',
      name: 'Implied Carbon Price',
      selected: false,
      data: data.IMPLIED_CARBON_PRICE,
      color: graphColors.IMPLIED_CARBON_PRICE,
      states: {
        select: {
          color: '#34a997',
        },
        hover: {
          color: '#34a997',
        },
      },
    } as SeriesOptionsType;

    const lme = {
      type: 'bar',
      name: 'LME',
      selected: false,
      data: data.LME,
      color: graphColors.LME,
      states: {
        select: {
          color: '#27e1bd',
        },
        hover: {
          color: '#27e1bd',
        },
      },
    } as SeriesOptionsType;

    const expectedSettlement = {
      type: 'bar',
      name: 'Expected Settlement',
      selected: false,
      data: data.EXPECTED_SETTLEMENT,
      color: graphColors.EXPECTED_SETTLEMENT,
      states: {
        select: {
          color: '#3a81b4',
        },
        hover: {
          color: '#3a81b4',
        },
      },
    } as SeriesOptionsType;

    const location = {
      type: 'bar',
      name: 'Location (ISO)',
      selected: false,
      data: data.LOCATION,
      color: graphColors.LOCATION,
      states: {
        select: {
          color: '#51a3d8',
        },
        hover: {
          color: '#51a3d8',
        },
      },
    } as SeriesOptionsType;

    return (this.buyerAnalyticsService.currentPreferences?.PRODUCT_TYPE ?? '') === PRODUCT_TYPE.RECS
      ? [impliedCarbonPrice, lme, location]
      : [location, expectedSettlement, projectAdditionality, lme, impliedCarbonPrice];
  }

  private getValuesFromData() {
    const formattedData = {
      EXPECTED_SETTLEMENT: [],
      LOCATION: [],
      IMPLIED_CARBON_PRICE: [],
      PROJECT_ADDITIONALITY: [],
      LME: [],
    };

    this.vppaRankings.forEach((rankings) => {
      Object.entries(formattedData).forEach(([key, value]) => {
        formattedData[key].push(rankings[key]);
      });
    });

    return formattedData;
  }

  public vppaRankingsChanged(oldValue, newValue: VppaRankings[]) {
    this.renderAllContractsGraph();
    if (this.chart) {
      this.checkSelectedVppas(this.chart.series);
    }
  }

  public checkOnEvent(event: any) {
    this.checkSelectedVppas(event.target.series);
  }

  public checkSelectedVppas(series) {
    if (this.chart && this.buyerAnalyticsService.selectedVppas.length) {
      this.$emit('enableProceedButton', true);
      series.forEach((series) => {
        this.buyerAnalyticsService.selectedVppas.forEach((vppa) => {
          const y = series.data.filter(
            (x) => x.category.substring(0, x.category.indexOf('-')) == vppa.UNIQUE_ID,
          );

          y[0].select(true, true);
        });
      });
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.notifier1.unsubscribe(this.handler1);
  }
}
